/* eslint-disable camelcase */
import _ from 'lodash'
import { graph } from '@adc/constants'
import { formatMoney } from '../../../../utils/formatters'
import interpolateString from '../../../../utils/interpolateString'
import ListingBadge, { BADGE_ORDER_PRIORITY, BADGE_MODE } from './base'
import { isTPSOnline, isREO } from './utils'

// TODO: update all apps, references in design system, and these here to use new graph field 'is_buyers_premium_available'
// currently @buyersPremiumAvailableValueReceived and @isBuyersPremiumAvailable are looking at two fields to maintain backwards compatibility
const buyersPremiumAvailableValueReceived = listing =>
  Object.prototype.hasOwnProperty.call(
    listing.listing_configuration || {},
    'is_buyers_premium_available'
  ) ||
  Object.prototype.hasOwnProperty.call(
    listing.listing_configuration || {},
    'buyer_premium_available'
  )

const isBuyersPremiumAvailable = listing =>
  listing.listing_configuration?.buyer_premium_available ||
  listing.listing_configuration?.is_buyers_premium_available

const premiumMinimumAmount = listing =>
  listing.program_configuration?.buyers_premium_minimum_amount

const premiumPrimaryAmount = listing =>
  listing.program_configuration?.buyers_premium_primary_amount

const hasPremiumStrategySet = listing =>
  premiumMinimumAmount(listing) > 0 || premiumPrimaryAmount(listing) > 0

const hasZeroBuyersPremiumState = listing =>
  premiumMinimumAmount(listing) === 0 && premiumPrimaryAmount(listing) === 0

/**
 * NOTE: This reads different to what we show in pdp bidding breakdown when we get there, because they are both
 * constructed independently by different product teams. If we expose this process both can use this logic to
 * calculate the same message once repipe in complete.
 */
const BUYERS_PREMIUM_EXPLANATIONS = {
  MESSAGE: 'Winner will be charged a fee for this property. %s',
  FIXED_AMOUNT: '%s regardless of Winning Bid Amount.',
  PERCENTAGE_WITH_GUARANTEED_MINIMUM:
    '%s% of Winning Bid Amount (A minimum of %s will apply)',
}

class BuyerPremiumAvailableBadge extends ListingBadge {
  static title = "Buyer's Premium"

  static titleIcon = 'dollar-sign'

  static textIcon = 'times'

  static contentPositive = false

  /**
   * This is here as a fallback for when we program configuration has buyers premium enabled, but the values for the
   * amounts don't seem correct (as is possible for new listings that are still being entered into the systemm so are
   * published, but not in their final form)
   */
  static summary = 'Applicable'

  static shouldBeIncluded(listing, badgeMode) {
    return (
      badgeMode === BADGE_MODE.EXPANDED &&
      isBuyersPremiumAvailable(listing) &&
      hasPremiumStrategySet(listing) &&
      (isTPSOnline(listing) || isREO(listing))
    )
  }

  // NOTE: This was hella complex in v1 through plat-property and I expect between that service and resi-pal, there
  // will be some minor bugs in the relationships to these things in the enxt few months.
  //
  // I am not sure what seller-level informatino this means for primary buyer. Likely need an override for `content`
  // above also. Originaly logic for the text that resi-pal sends through currently is
  // https://git.infra.aucn.io/projects/PA/repos/platform-property/browse/src/main/java/com/auction/acp/property/das/service/impl/PropertyServiceImpl.java#1663,1665,1668,1681,1685-1686,1692-1693,1698,1703,1708,1714,1716
  get summary() {
    const { listing } = this.props

    const buyersPremiumMinAmount = premiumMinimumAmount(listing)
    const buyersPremiumAmount = premiumPrimaryAmount(listing)

    const type = _.get(
      listing.program_configuration,
      'buyers_premium_commission_type'
    )

    return [
      type === graph.enums.CommissionStrategyType.PERCENTAGE &&
      buyersPremiumAmount
        ? `${buyersPremiumAmount}%`
        : '',
      buyersPremiumMinAmount ? formatMoney(buyersPremiumMinAmount) : '',
    ]
      .filter(Boolean)
      .join(' or ')
  }

  get content() {
    const {
      buyers_premium_commission_type,
      buyers_premium_minimum_amount,
      buyers_premium_primary_amount,
    } = this.props.listing.program_configuration || {}

    let suffix = ''

    if (
      buyers_premium_commission_type ===
      graph.enums.CommissionStrategyType.DOLLAR
    ) {
      suffix = interpolateString(BUYERS_PREMIUM_EXPLANATIONS.FIXED_AMOUNT, [
        formatMoney(buyers_premium_primary_amount),
      ])
    } else if (
      buyers_premium_commission_type ===
      graph.enums.CommissionStrategyType.PERCENTAGE
    ) {
      suffix = interpolateString(
        BUYERS_PREMIUM_EXPLANATIONS.PERCENTAGE_WITH_GUARANTEED_MINIMUM,
        [
          buyers_premium_primary_amount,
          formatMoney(buyers_premium_minimum_amount),
        ]
      )
    }

    return interpolateString(BUYERS_PREMIUM_EXPLANATIONS.MESSAGE, [suffix])
  }
}

/**
 * This one is a little tricky as the condensed version is showing here when as a "positive case" by the fact
 * there is an absence of this field.  Hence the title and icon and negated to indicate no premium
 * (which is a good thing)
 *
 * NOTE: Just a reminder again. We have to handle complex cases here. There is no buyers premium applicable if we indicate
 * it's not available but that the value is 0, which is good. That is treated as a different case in the product than it
 * not being available and having no values at all (nobody seems to remember or find a reason _why_ this extra logic is
 * there or if it's worth the contortion we have to code to make it work).
 */
class BuyerPremiumUnavailableBadge extends ListingBadge {
  static title = "Buyer's Premium"

  static titleIcon = 'dollar-sign'

  static condensedTitle = "No Buyer's Premium"

  static renderPriority = BADGE_ORDER_PRIORITY.FOURTH

  static condensedTitleIcon = 'ban'

  static contentPositive = false

  static content =
    "Winner will not be charged a Buyer's Premium for this property."

  static summary = 'Not Applicable'

  static shouldBeIncluded(listing) {
    return (
      (isTPSOnline(listing) || isREO(listing)) &&
      ((buyersPremiumAvailableValueReceived(listing) &&
        !isBuyersPremiumAvailable(listing)) ||
        (isBuyersPremiumAvailable(listing) &&
          hasZeroBuyersPremiumState(listing)))
    )
  }

  get contentPositive() {
    return (
      isBuyersPremiumAvailable(this.props.listing) &&
      hasZeroBuyersPremiumState(this.props.listing)
    )
  }

  get summary() {
    if (!isBuyersPremiumAvailable(this.props.listing)) {
      return null
    }

    const amount = premiumMinimumAmount(this.props.listing)

    return _.isFinite(amount) ? formatMoney(amount) : null
  }
}

export { BuyerPremiumAvailableBadge, BuyerPremiumUnavailableBadge }
